import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Stack,
  Box
} from '@mui/material';

interface PrivacyNoticeDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const cellSx = { verticalAlign: 'top', py: 0.5, px: 1, border: 'none' } as const;
const labelSx = { ...cellSx, fontWeight: 600, whiteSpace: 'nowrap', width: '40%' } as const;

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <TableRow>
      <TableCell sx={labelSx}>
        <Typography variant="body2">{label}</Typography>
      </TableCell>
      <TableCell sx={cellSx}>
        <Typography variant="body2">{value}</Typography>
      </TableCell>
    </TableRow>
  );
}

const PROCESSING_ACTIONS = [
  'сбор', 'запись', 'систематизация', 'накопление', 'хранение',
  'уточнение (обновление, изменение)', 'извлечение', 'использование',
  'передача (предоставление, доступ)', 'блокирование', 'удаление', 'уничтожение'
];

export default function PrivacyNoticeDialog({ open, onClose, onAccept }: PrivacyNoticeDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ p: 2 }}>
        <Typography variant="h6" align="center" fontWeight={700}>
          УВЕДОМЛЕНИЕ
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary">
          об обработке (о намерении осуществлять обработку) персональных данных
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack sx={{ p: 2 }} spacing={3}>
          {/* Общие сведения */}
          <Table size="small">
            <TableBody>
              <InfoRow label="Тип оператора" value="Индивидуальный предприниматель" />
              <InfoRow label="Наименование оператора" value="ИП Шиф Алексей Александрович" />
              <InfoRow label="Адрес оператора" value="192029, Санкт-Петербург г, вн.тер.г. муниципальный округ Невская застава, ул. Ольминского, д.20, кв. 15" />
              <InfoRow label="Электронная почта" value="roman_vb@mail.ru" />
              <InfoRow label="ИНН" value="781141475993" />
              <InfoRow label="ОГРНИП" value="322784700155505 от 24.05.2022" />
              <InfoRow label="Регионы обработки" value="все субъекты Российской Федерации" />
            </TableBody>
          </Table>

          <Divider />

          {/* Цель №1 */}
          <div>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Цель обработки данных № 1
            </Typography>
            <Typography variant="body2" gutterBottom>
              Подготовка, заключение и исполнение договора на предоставление сервиса для соискателей.
            </Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Категории персональных данных:
            </Typography>
            <Typography variant="body2" component="div">
              фамилия, имя, отчество; год, месяц, дата и место рождения; пол; адрес электронной почты;
              адрес места жительства; номер телефона; профессия; должность; сведения о трудовой деятельности
              (в том числе стаж работы, данные о трудовой занятости); сведения об образовании;
              фото-видео изображение лица; иные персональные данные (сведения из резюме; сведения о карьере
              и квалификации; результаты тестирования на сайте; видеоинтервью с клиентом).
            </Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Категории субъектов:
            </Typography>
            <Typography variant="body2">Соискатели, Клиенты</Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Правовое основание:
            </Typography>
            <Typography variant="body2">
              Обработка осуществляется с согласия субъекта персональных данных, а также для исполнения
              договора, стороной которого является субъект персональных данных.
            </Typography>
          </div>

          <Divider />

          {/* Цель №2 */}
          <div>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Цель обработки данных № 2
            </Typography>
            <Typography variant="body2" gutterBottom>
              Подготовка, заключение и исполнение договора на предоставление сервиса для работодателей.
            </Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Категории персональных данных:
            </Typography>
            <Typography variant="body2" component="div">
              фамилия, имя, отчество; адрес электронной почты; номер телефона; должность;
              фото-видео изображение лица; иные персональные данные (место работы; страна и город
              местонахождения; сведения из аудио-звонка / видео встречи; видеоинтервью с клиентом).
            </Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Категории субъектов:
            </Typography>
            <Typography variant="body2">
              Клиенты, представители потенциальных клиентов/ЮЛ (ИП) — работодатели, спикеры для HR-интервью
            </Typography>
          </div>

          <Divider />

          {/* Цель №3 */}
          <div>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Цель обработки данных № 3
            </Typography>
            <Typography variant="body2" gutterBottom>
              Обеспечение функционирования и улучшения работы веб-сайта, включая адаптацию контента
              под предпочтения пользователя, проведение аналитики посещаемости, улучшение пользовательского
              опыта, а также осуществление взаимодействия с посетителями сайта.
            </Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Категории персональных данных:
            </Typography>
            <Typography variant="body2">
              фамилия, имя, отчество; адрес электронной почты; номер телефона; сведения,
              собираемые посредством метрических программ; cookie.
            </Typography>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Категории субъектов:
            </Typography>
            <Typography variant="body2">Посетители сайта</Typography>
          </div>

          <Divider />

          {/* Перечень действий */}
          <div>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Перечень действий с персональными данными:
            </Typography>
            <Typography variant="body2">
              {PROCESSING_ACTIONS.join(', ')}.
            </Typography>
          </div>

          <Divider />

          {/* Меры безопасности */}
          <div>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Меры по обеспечению безопасности:
            </Typography>
            <Typography variant="body2" component="div">
              1) Определено лицо, ответственное за организацию обработки персональных данных;<br />
              2) Разработан документ, определяющий политику оператора в отношении обработки ПДн;<br />
              3) Используется антивирусное ПО и производится регулярное обновление программного обеспечения;<br />
              4) Учетная запись защищена паролем, удовлетворяющим рекомендациям ФСТЭК России;<br />
              5) Доступ к данным ограничен и осуществляется только в рамках исполнения обязательств по договору.
            </Typography>
          </div>

          <Divider />

          {/* Условия прекращения */}
          <div>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Условия прекращения обработки персональных данных:
            </Typography>
            <Typography variant="body2" component="div">
              — достижение целей обработки ПДн;<br />
              — отзыв согласия субъекта ПДн на обработку;<br />
              — выявление неправомерной обработки ПДн;<br />
              — истечение установленного срока хранения ПДн;<br />
              — ликвидация / прекращение деятельности Оператора.
            </Typography>
          </div>

          <Divider />

          {/* Хранение */}
          <div>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Местонахождение баз данных:
            </Typography>
            <Typography variant="body2">
              Россия. Хранение осуществляется на инфраструктуре ООО «ВК» (г. Москва),
              ООО «Яндекс.Облако» (г. Владимир, г. Сасово, г. Калуга, г. Мытищи),
              АО «Селектел» (г. Санкт-Петербург).
            </Typography>
          </div>

          <Divider />

          <Typography variant="body2" fontWeight={600}>
            Трансграничная передача персональных данных не осуществляется.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Дата начала обработки персональных данных: 08.04.2026
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Ответственное лицо: Шиф Алексей Александрович, roman_vb@mail.ru
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={4} sx={{ p: 2, width: '100%' }}>
          {/* <Button onClick={onClose} variant="contained" size="large" color="error" fullWidth>
            Отказаться
          </Button>
          <Button onClick={onAccept} variant="contained" size="large" fullWidth>
            Принимаю условия
          </Button> */}
          <Button onClick={onClose} variant="contained" size="large" fullWidth>
            Закрыть
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
